import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { UserAvatar } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  LayoutGrid,
  UserCircle2,
  Users,
  LogOut,
  Plus,
  Trash2,
  Camera,
  Wallet,
  RefreshCw,
  ChevronDown,
  DatabaseBackup,
  Mail as MailIcon,
} from "lucide-react";
import { useState, useEffect, Fragment } from "react";
import TaskModal from "@/components/TaskModal";
import { AMM_TABS } from "@/components/AmmTabs";
import NotificationsBell from "@/components/NotificationsBell";
import NotificationPermissionPrompt from "@/components/NotificationPermissionPrompt";
import NotificationsManager from "@/components/NotificationsManager";
import AssistantWidget from "@/components/AssistantWidget";
import BrandLogo from "@/components/BrandLogo";
import { MailBadge } from "@/components/MailBadge";
import { useMail } from "@/context/MailContext";
import { useSettings } from "@/context/SettingsContext";
import { useNotifPrefs, pushNotifPrefsToServer } from "@/lib/notifPrefs";
import { subscribeToPush } from "@/lib/notify";
import { todayISO } from "@/lib/tp";
import { isCliente, CLIENTE_ALLOWED_PATHS } from "@/lib/roles";
import { toast } from "sonner";
import { Settings, Bell } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";

const NAV = [
  { to: "/", label: "Clienti", icon: LayoutGrid, testId: "nav-dashboard" },
  { to: "/amministrazione", label: "Amministrazione", icon: Wallet, testId: "nav-admin" },
  { to: "/il-mio-lavoro", label: "Il mio lavoro", icon: UserCircle2, testId: "nav-il-mio-lavoro" },
  { to: "/team", label: "Team", icon: Users, testId: "nav-users" },
  { to: "/shooting", label: "Shooting", icon: Camera, testId: "nav-shooting" },
  { to: "/cestino", label: "Cestino", icon: Trash2, testId: "nav-cestino" },
  { to: "/backup", label: "Backup", icon: DatabaseBackup, testId: "nav-backup" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { tasks, reload, isTaskPanelOpen } = useData();
  const { unread: mailUnread } = useMail();
  const [refreshing, setRefreshing] = useState(false);

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      await reload();
    } finally {
      setRefreshing(false);
    }
  };

  // Auto-refresh ogni 10 minuti — sospeso se è aperto un pannello di creazione/modifica task
  useEffect(() => {
    const iv = setInterval(() => {
      if (isTaskPanelOpen()) return;
      reload().catch(() => {});
    }, 10 * 60 * 1000);
    return () => clearInterval(iv);
  }, [reload, isTaskPanelOpen]);
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [taskOpen, setTaskOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notifMgrOpen, setNotifMgrOpen] = useState(false);
  const { prefs: notifPrefs, toggle: toggleNotifPref } = useNotifPrefs();
  const [permState, setPermState] = useState(
    typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported"
  );
  const [pwForm, setPwForm] = useState({ old_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    if (settingsOpen && typeof window !== "undefined" && "Notification" in window) {
      setPermState(Notification.permission);
    }
  }, [settingsOpen]);

  const requestBrowserPermission = async () => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    try {
      const res = await Notification.requestPermission();
      setPermState(res);
      if (res === "granted") {
        toggleNotifPref("browser", true);
        pushNotifPrefsToServer(api, { ...notifPrefs, browser: true });
        subscribeToPush(api);
        toast.success("Notifiche attivate su questo dispositivo");
      } else if (res === "denied") {
        toast("Permesso bloccato", { description: "Sblocca le notifiche dalle impostazioni del browser per questa app." });
      }
    } catch {
      /* ignore */
    }
  };

  const enableBrowserNotifs = async (val) => {
    if (val && typeof window !== "undefined" && "Notification" in window && Notification.permission !== "granted") {
      try {
        const res = await Notification.requestPermission();
        setPermState(res);
        if (res !== "granted") {
          toast("Permesso negato dal browser", { description: "Attiva le notifiche dalle impostazioni del browser per questa app." });
          return;
        }
      } catch {
        return;
      }
    }
    toggleNotifPref("browser", val);
    pushNotifPrefsToServer(api, { ...notifPrefs, browser: val });
    if (val) subscribeToPush(api);
  };
  const changePref = (key, val) => {
    toggleNotifPref(key, val);
    pushNotifPrefsToServer(api, { ...notifPrefs, [key]: val });
  };

  const changePassword = async () => {
    if (pwForm.new_password.length < 6) return toast.error("Nuova password minimo 6 caratteri");
    if (pwForm.new_password !== pwForm.confirm) return toast.error("Le password non coincidono");
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", {
        old_password: pwForm.old_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password aggiornata");
      setPwForm({ old_password: "", new_password: "", confirm: "" });
      setSettingsOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setPwSaving(false);
    }
  };

  const isAdmin = user && user.role === "admin";
  const clienteMode = isCliente(user);

  // Route guard for cliente role: only allow /il-mio-lavoro
  useEffect(() => {
    if (!clienteMode) return;
    const allowed = CLIENTE_ALLOWED_PATHS.some((p) => location.pathname === p);
    if (!allowed) {
      toast.error("Non hai l'autorizzazione per accedere a questa sezione");
      navigate("/il-mio-lavoro", { replace: true });
    }
  }, [clienteMode, location.pathname, navigate]);

  const NAV_ITEMS = clienteMode
    ? NAV.filter((n) => n.to === "/il-mio-lavoro")
    : NAV.filter((n) => ((n.to !== "/amministrazione" && n.to !== "/backup") || isAdmin));

  const myTodayCount = tasks.filter(
    (t) => t.assignee_ids?.includes(user.id) && t.due_date === todayISO() && t.status !== "completato",
  ).length;

  const [admOpen, setAdmOpen] = useState(location.pathname.startsWith("/amministrazione"));
  useEffect(() => {
    if (location.pathname.startsWith("/amministrazione")) setAdmOpen(true);
  }, [location.pathname]);

  return (
    <div className="min-h-screen tp-bg text-white flex">
      {/* Sidebar - desktop */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-white/5 p-4 gap-6 sticky top-0 h-screen">
        <div className="flex items-center gap-3 px-2 pt-2">
          <BrandLogo size={36} rounded="rounded-xl" />
          <div>
            <div className="text-sm font-semibold tp-brand-text">
              {settings.app_name || "Hi-Work"}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Studio</div>
          </div>
        </div>

        <nav className="flex flex-col gap-1">
          {NAV_ITEMS.map(({ to, label, icon: Icon, testId }) => {
            if (to === "/amministrazione") {
              const active = location.pathname.startsWith("/amministrazione");
              return (
                <div key={to}>
                  <button
                    type="button"
                    data-testid={testId}
                    onClick={() => setAdmOpen((v) => !v)}
                    className={`w-full group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                      active ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <Icon size={16} />
                    <span className="flex-1 text-left">{label}</span>
                    <ChevronDown size={15} className={`transition-transform ${admOpen ? "rotate-180" : ""}`} />
                  </button>
                  {admOpen && (
                    <div className="mt-1 ml-3 pl-3 border-l border-white/10 flex flex-col gap-0.5">
                      {AMM_TABS.map((s) => {
                        const SIcon = s.icon;
                        return (
                          <NavLink
                            key={s.to}
                            to={s.to}
                            data-testid={`${s.testId}-side`}
                            className={({ isActive }) =>
                              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-colors ${
                                isActive ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
                              }`
                            }
                          >
                            <SIcon size={14} />
                            {s.label}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Fragment key={to}>
                <NavLink
                  to={to}
                  end={to === "/"}
                  data-testid={testId}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                      isActive
                        ? "bg-white/10 text-white"
                        : "text-zinc-400 hover:text-white hover:bg-white/5"
                    }`
                  }
                >
                  <Icon size={16} />
                  <span className="flex-1">{label}</span>
                  {to === "/il-mio-lavoro" && myTodayCount > 0 && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md tp-brand-gradient text-white shadow-md shadow-fuchsia-600/30">
                      {myTodayCount}
                    </span>
                  )}
                </NavLink>
                {to === "/team" && isAdmin && (
                  <NavLink
                    to="/mail"
                    data-testid="nav-mail"
                    className={({ isActive }) =>
                      `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                        isActive ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
                      }`
                    }
                  >
                    <MailIcon size={16} />
                    <span className="flex-1">Mail</span>
                    <MailBadge count={mailUnread} testId="nav-mail-badge" />
                  </NavLink>
                )}
              </Fragment>
            );
          })}
        </nav>

        <div className="mt-auto flex flex-col gap-2">
          <Button
            data-testid="refresh-tasks-button"
            onClick={doRefresh}
            disabled={refreshing}
            variant="outline"
            className="w-full h-10 rounded-xl border-fuchsia-400/40 bg-transparent text-fuchsia-300 hover:bg-fuchsia-500/10 hover:text-white text-xs tracking-widest uppercase"
          >
            <RefreshCw size={14} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Aggiorno..." : "Aggiorna task"}
          </Button>
          <Button
            data-testid="add-task-button"
            onClick={() => setTaskOpen(true)}
            className="tp-brand-btn w-full h-11 rounded-xl"
          >
            <Plus size={16} className="mr-2" /> Nuova task
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header
          className="sticky top-0 z-30 tp-glass border-b border-white/5"
          style={{ paddingTop: "env(safe-area-inset-top)" }}
        >
          <div className="px-4 sm:px-8 h-16 flex items-center gap-4">
            <div className="lg:hidden flex items-center gap-2">
              <BrandLogo size={32} rounded="rounded-lg" editable={false} />
              <span className="font-semibold text-sm tp-brand-text">
                {settings.app_name || "Hi-Work"}
              </span>
            </div>

            <div className="flex-1" />

            <Button
              data-testid="add-task-button-header"
              onClick={() => setTaskOpen(true)}
              className="tp-brand-btn h-9 rounded-full px-3 sm:px-4 text-sm"
            >
              <Plus size={14} className="sm:mr-1.5" />
              <span className="hidden sm:inline">Task</span>
            </Button>

            <NotificationsBell />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu"
                  className="flex items-center gap-2 rounded-full p-1 pr-3 hover:bg-white/5 transition-colors"
                >
                  <UserAvatar user={user} size={32} />
                  <span className="hidden sm:inline text-sm text-zinc-200">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="tp-glass-strong border-white/10 text-white w-56">
                <DropdownMenuLabel className="text-zinc-400 text-xs uppercase tracking-widest">
                  {user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  data-testid="open-settings"
                  onClick={() => setSettingsOpen(true)}
                  className="focus:bg-white/10 text-zinc-200"
                >
                  <Settings size={14} className="mr-2" /> Impostazioni
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={async () => {
                    await logout();
                    navigate("/login");
                  }}
                  className="focus:bg-white/10 text-red-400"
                >
                  <LogOut size={14} className="mr-2" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 px-4 sm:px-8 py-6 pb-24 lg:pb-8">{children}</main>

        {/* Bottom nav - mobile: Cestino nascosto, "Il mio lavoro" centrale ed elevato */}
        <nav
          className="lg:hidden fixed bottom-0 inset-x-0 z-30 tp-glass border-t border-white/10"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div
            className={`${
              (() => {
                const items = NAV_ITEMS.filter((n) => n.to !== "/cestino" && n.to !== "/backup");
                if (items.length <= 1) return "flex justify-center";
                return `grid ${items.length === 5 ? "grid-cols-5" : items.length === 4 ? "grid-cols-4" : items.length === 3 ? "grid-cols-3" : "grid-cols-2"}`;
              })()
            } items-end`}
          >
            {(() => {
              // Ordine desiderato mobile: Clienti, Team, Il mio lavoro (centro), Amministrazione, Shooting
              const order = ["/", "/team", "/il-mio-lavoro", "/amministrazione", "/shooting"];
              const items = NAV_ITEMS
                .filter((n) => n.to !== "/cestino" && n.to !== "/backup")
                .slice()
                .sort((a, b) => order.indexOf(a.to) - order.indexOf(b.to));
              return items.map(({ to, label, icon: Icon, testId }) => {
                const isCenter = to === "/il-mio-lavoro";
                if (isCenter) {
                  return (
                    <NavLink
                      key={to}
                      to={to}
                      end={to === "/"}
                      data-testid={`${testId}-mobile`}
                      className="flex flex-col items-center justify-end pb-1"
                    >
                      {({ isActive }) => (
                        <>
                          <div
                            className={`w-14 h-14 rounded-full flex items-center justify-center -mt-6 ring-4 ring-[#09090B] shadow-lg transition-all ${
                              isActive
                                ? "tp-brand-gradient shadow-fuchsia-600/50 scale-105"
                                : "bg-white/10 shadow-fuchsia-600/20 ring-4"
                            }`}
                          >
                            <Icon size={24} className="text-white" />
                          </div>
                          <span
                            className={`mt-1 text-[9px] font-semibold ${
                              isActive ? "text-white" : "text-zinc-400"
                            }`}
                          >
                            {label}
                          </span>
                        </>
                      )}
                    </NavLink>
                  );
                }
                return (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === "/"}
                    data-testid={`${testId}-mobile`}
                    className={({ isActive }) =>
                      `flex flex-col items-center gap-1 py-2.5 text-[9px] ${
                        isActive ? "text-white" : "text-zinc-500"
                      }`
                    }
                  >
                    <span className="relative">
                      <Icon size={16} />
                      {to === "/amministrazione" && mailUnread > 0 && (
                        <span className="absolute -top-1.5 -right-2">
                          <MailBadge count={mailUnread} className="!px-1 !py-0 text-[8px]" testId="nav-admin-mail-badge-mobile" />
                        </span>
                      )}
                    </span>
                    <span className="truncate max-w-[56px]">{label}</span>
                  </NavLink>
                );
              });
            })()}
          </div>
        </nav>
      </div>

      <TaskModal open={taskOpen} onOpenChange={setTaskOpen} />
      <NotificationPermissionPrompt />

      {/* Settings dialog: cambio password */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="tp-glass-strong border-white/10 text-white max-w-sm max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Impostazioni account</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-zinc-400">
                Password attuale
              </Label>
              <Input
                type="password"
                data-testid="settings-old-password"
                value={pwForm.old_password}
                onChange={(e) => setPwForm({ ...pwForm, old_password: e.target.value })}
                className="bg-white/5 border-white/10 text-white h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-zinc-400">
                Nuova password
              </Label>
              <Input
                type="password"
                data-testid="settings-new-password"
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                className="bg-white/5 border-white/10 text-white h-11"
                placeholder="Almeno 6 caratteri"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-zinc-400">
                Conferma nuova password
              </Label>
              <Input
                type="password"
                data-testid="settings-confirm-password"
                value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                className="bg-white/5 border-white/10 text-white h-11"
              />
            </div>
            <Button
              data-testid="settings-save-password"
              onClick={changePassword}
              disabled={pwSaving}
              className="tp-brand-btn w-full rounded-xl"
            >
              {pwSaving ? "Salvo..." : "Cambia password"}
            </Button>

            {/* Notifiche */}
            <div className="pt-4 mt-2 border-t border-white/10 space-y-3">
              <div className="flex items-center gap-2">
                <Bell size={15} className="text-fuchsia-300" />
                <span className="text-sm font-medium text-white">Notifiche</span>
              </div>

              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-white">Notifiche desktop</div>
                  <div className="text-[11px] text-zinc-500">Avvisi del browser anche fuori dall'app</div>
                </div>
                <Switch
                  data-testid="pref-browser"
                  checked={!!notifPrefs.browser && permState === "granted"}
                  onCheckedChange={enableBrowserNotifs}
                />
              </div>

              {permState !== "granted" && permState !== "unsupported" && (
                <div
                  data-testid="notif-permission-status"
                  className="rounded-xl border border-fuchsia-500/20 bg-fuchsia-500/[0.06] p-3 space-y-2"
                >
                  {permState === "denied" ? (
                    <>
                      <div className="text-[12px] text-zinc-300 leading-relaxed">
                        Le notifiche sono <span className="text-fuchsia-300 font-medium">bloccate</span> per questa app.
                        Su Mac apri <span className="text-white">Chrome → Impostazioni sito</span> (icona vicino all'indirizzo)
                        e imposta <span className="text-white">Notifiche: Consenti</span>, poi riapri l'app.
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-[12px] text-zinc-300 leading-relaxed">
                        Il permesso notifiche non è ancora stato attivato su questo dispositivo.
                      </div>
                      <Button
                        data-testid="notif-request-permission"
                        onClick={requestBrowserPermission}
                        className="tp-brand-btn w-full h-9 rounded-lg text-sm"
                      >
                        <Bell size={13} className="mr-2" /> Attiva su questo dispositivo
                      </Button>
                    </>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-white">Avvisi task</div>
                  <div className="text-[11px] text-zinc-500">Task assegnate o completate</div>
                </div>
                <Switch
                  data-testid="pref-task"
                  checked={!!notifPrefs.task}
                  onCheckedChange={(v) => changePref("task", v)}
                />
              </div>

              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-white">Avvisi nuove email</div>
                  <div className="text-[11px] text-zinc-500">Avviso in-app all'arrivo di posta</div>
                </div>
                <Switch
                  data-testid="pref-mail"
                  checked={!!notifPrefs.mail}
                  onCheckedChange={(v) => changePref("mail", v)}
                />
              </div>

              <Button
                data-testid="open-notif-manager"
                variant="outline"
                onClick={() => { setSettingsOpen(false); setNotifMgrOpen(true); }}
                className="w-full h-10 rounded-xl border-white/10 bg-transparent text-zinc-200 hover:bg-white/5"
              >
                <Bell size={14} className="mr-2" /> Gestisci notifiche
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <NotificationsManager open={notifMgrOpen} onOpenChange={setNotifMgrOpen} />
      <AssistantWidget />
    </div>
  );
}
